<template>
  <div>
    <div class="customerSearchRow">
      <wa-button
        variant="neutral"
        appearance="outlined"
        size="m"
        class="qrScanTrigger"
        data-testid="qr-scan-trigger"
        :title="$t('qrScanner.title')"
        @click="showQrScanner = true"
      >
        <PhQrCode :size="22" />
      </wa-button>
      <div class="dropdown-selection">
        <wa-input
          ref="searchInputRef"
          v-model="query"
          :placeholder="$t('customer.find_customer')"
          size="m"
          enterkeyhint="search"
          inputmode="search"
          class="customerSearch"
          data-testid="customer-search-input"
          with-clear
          autocomplete="off"
          @click="handleClick"
          @keydown="handleSearchKeydown"
          @wa-clear="handleClearInput"
          @focus="handleCustomersFromLocalStorage"
        >
          <wa-icon library="bootstrap" slot="start" name="search"></wa-icon>
        </wa-input>
        <wa-dropdown
          ref="searchDropdownRef"
          class="searchDropdown"
          :open="uiStore.showCustomerSearchDialog"
          placement="bottom-end"
          style="z-index: 699"
          @wa-after-show="refocusSearchInput"
          @wa-after-hide="handleCloseDropdown"
        >
          <!-- Web Awesome only opens a dropdown that has a slotted trigger. The search input
               above drives `open`, so an empty anchor stands in for the trigger. -->
          <span slot="trigger" class="searchDropdownAnchor" aria-hidden="true"></span>
          <wa-dropdown-item
            class="add-new-customer"
            data-testid="add-new-customer-menu"
            @click="$emit('openAddNewCustomer')"
          >
            <PhPlus slot="icon" :size="16" weight="bold" />
            {{ $t('customer.add_new_customer') }}
          </wa-dropdown-item>
          <template v-if="searchNoResults && !isSearching">
            <wa-divider></wa-divider>
            <wa-dropdown-item class="no-customer-found" data-testid="no-customer-found-result"
              ><wa-icon library="bootstrap" slot="icon" name="exclamation-triangle"></wa-icon
              >{{ $t('customer.no_customer_found') }}</wa-dropdown-item
            >
          </template>
          <div
            v-if="isSearching"
            style="padding-left: 24px; display: flex; align-items: center; gap: 12px"
          >
            <wa-spinner></wa-spinner>{{ $t('globals.searching') }}...
          </div>
          <template v-if="customers.length > 0">
            <wa-divider></wa-divider>
            <wa-dropdown-item
              v-for="c in customers.slice(0, 3)"
              :key="c.d"
              class="resItem"
              @click="loadCustomer(c.id)"
            >
              <div class="resItem__layout">
                {{ c.first_name }} {{ c.last_name }}
                <div class="text-color-neutral">{{ c.email }}</div>
              </div>
            </wa-dropdown-item>
            <div
              v-if="customers.length > 3"
              style="
                padding: var(--wa-space-s) var(--wa-space-l) var(--wa-space-s) var(--wa-space-l);
              "
            >
              <wa-button variant="neutral" appearance="outlined" size="s" @click="showMore = true"
                >{{ $t('globals.all') }} ({{ customers.length }})</wa-button
              >
            </div>
          </template>
          <template v-else-if="!isSearching && !searchNoResults && lastClients.length > 0">
            <wa-divider></wa-divider>
            <div class="menu-label last-clients-label">{{ $t('customer.last_clients') }}</div>
            <wa-dropdown-item v-for="(c, i) in lastClients" :key="i" @click="loadCustomer(c.id)">
              <div
                class="menu-item"
                :class="[
                  {
                    'menu-item--danger': [
                      CUSTOMER_RESTRICTION.BANNED,
                      CUSTOMER_RESTRICTION.BLOCKED
                    ].includes(c?.restrictions)
                  }
                ]"
              >
                <div class="resItem__layout">
                  {{ c.firstName }} {{ c.lastName }}
                  <div class="text-color-neutral">{{ c.email }}</div>
                </div>
                <template v-if="c.restrictions === CUSTOMER_RESTRICTION.PREPAYMENT">
                  <wa-tag variant="warning" size="s">{{
                    $t('customer.form.restriction.prepayment.title')
                  }}</wa-tag>
                </template>
                <template v-if="c.restrictions === CUSTOMER_RESTRICTION.BANNED">
                  <wa-tag variant="danger" size="s">{{
                    $t('customer.form.restriction.banned.title')
                  }}</wa-tag>
                </template>
                <template v-if="c.restrictions === CUSTOMER_RESTRICTION.BLOCKED">
                  <wa-tag variant="danger" size="s">{{
                    $t('customer.form.restriction.blocked.title')
                  }}</wa-tag>
                </template>
              </div>
            </wa-dropdown-item>
          </template>
        </wa-dropdown>
      </div>
    </div>

    <QRCodeScanner
      v-if="showQrScanner"
      @codeScanned="handleQrScanned"
      @closeScanner="showQrScanner = false"
    />

    <wa-dialog
      light-dismiss
      :open="showMore"
      :label="`${$t('customer.allResults')} (${customers.length})`"
      style="--width: 880px"
      no-footer
      @wa-request-close="((showMore = false), (customers = []))"
    >
      <EasyDataTable
        :headers="[
          {
            text: $t('customer.table.nameEmail'),
            value: 'first_name',
            sortable: true
          },
          { text: $t('customer.address'), value: 'address', sortable: false },
          {
            text: $t('customer.form.label.country'),
            value: 'country',
            sortable: true
          },
          { text: $t('customer.form.label.birth_date'), value: 'birth_date', sortable: true },
          { text: $t('customer.table.action'), value: 'action', sortable: false }
        ]"
        :items="customers"
        :hide-rows-per-page="true"
        :rows-per-page="100"
        :hide-footer="true"
        table-class-name="wings-table customer-table"
      >
        <template #item-first_name="slotProps">
          {{ slotProps.first_name }} {{ slotProps.last_name }}
          <div class="text-color-neutral">{{ slotProps.email }}</div>
        </template>
        <template #item-birth_date="slotProps">
          {{ slotProps.birth_date ? timestampToYYYYMMDD(slotProps.birth_date) : '' }}
          <div v-if="slotProps.birth_date" class="text-color-neutral">
            {{ getCurrentAge(timestampToYYYYMMDD(slotProps.birth_date), 'dd.mm.yyyy') }}
            {{ $t('globals.years') }}
          </div>
        </template>
        <template #item-address="slotProps">
          {{ slotProps.address_line_1 }}
          <div class="text-color-neutral">
            {{ slotProps.address_zip_code }} {{ slotProps.address_city }}
          </div>
        </template>
        <template #item-action="slotProps">
          <wa-button
            variant="neutral"
            appearance="outlined"
            @click="
              (loadCustomer(slotProps.id), (showMore = false), (customers = []), (query = ''))
            "
            >{{ $t('globals.select') }}</wa-button
          >
        </template>
      </EasyDataTable>
    </wa-dialog>

    <wa-dialog
      light-dismiss
      v-if="uiStore.showCustomerBannedDialog"
      :label="$t('cart.alert.attention')"
      class="dialog-overview"
      open
      @wa-request-close="uiStore.showCustomerBannedDialog = false"
    >
      <img src="https://media.giphy.com/media/l2Je3fAJ02BkvLYEE/giphy.gif" alt="banned" />
      {{ $t('cart.alert.customerResctrictionBanned') }}
      <wa-button slot="footer" variant="brand" @click="uiStore.showCustomerBannedDialog = false">{{
        $t('globals.close')
      }}</wa-button>
    </wa-dialog>

    <wa-dialog
      light-dismiss
      v-if="uiStore.showEmailBannedOrInvalid"
      :label="$t('cart.alert.attention')"
      class="dialog-overview"
      open
      @wa-request-close="uiStore.showEmailBannedOrInvalid = false"
    >
      {{ $t('cart.alert.customerEmailInvalidOrBanned') }}
      <wa-button
        slot="footer"
        variant="neutral"
        appearance="outlined"
        @click="uiStore.showEmailBannedOrInvalid = false"
        >{{ $t('globals.close') }}</wa-button
      ><wa-button
        slot="footer"
        variant="brand"
        @click="
          ((customerStore.isCustomerProfileEdit = true),
          (uiStore.currentCustomerDrawer = 'profile'),
          (uiStore.showCustomerCreationEditDialog = true),
          (uiStore.showEmailBannedOrInvalid = false))
        "
        >{{ $t('customer.menu.customer_profile') }}</wa-button
      >
    </wa-dialog>

    <CustomerCartDialog
      :is-choose-cart-visible="isChooseCartVisible && !hideChooseCartDialog"
      :is-loading-customer="isLoadingCustomer"
      @close-dialog="closeDialog"
      @wa-request-close="uiStore.showCustomerBannedDialog = false"
    />
  </div>
</template>

<script setup>
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js'
import '@awesome.me/webawesome/dist/components/format-date/format-date.js'
import '@awesome.me/webawesome/dist/components/icon/icon.js'
import '@awesome.me/webawesome/dist/components/input/input.js'
import '@awesome.me/webawesome/dist/components/spinner/spinner.js'

import CustomerCartDialog from '@/components/Customer/CustomerCartDialog/CustomerCartDialog.vue'
import { useCustomer } from '@/composables/useCustomer.js'
import {
  getCustomersFromLocalStorage,
  setCustomerInTheLocalStorage
} from '@/composables/useCustomerLocalStorage'
import { useNotification } from '@/composables/useNotification.js'
import { CUSTOMER_RESTRICTION, GUEST_USER_EMAILS } from '@/constants/customer.js'
import { useCartStore } from '@/stores/cart.js'
import { useConfiguratorStore } from '@/stores/configurator'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device.js'
import { useUiStore } from '@/stores/ui.js'
import { getCurrentAge, timestampToYYYYMMDD } from '@/utils/dateTime'
import { PhPlus } from '@phosphor-icons/vue'
import { watchDebounced } from '@vueuse/core'
import { useTypesense } from '@/composables/useTypesense'
import { usePackageDeal } from '@/composables/usePackageDeal'
import { useStockTransfer } from '@/composables/useStockTransfer.js'
import { defineAsyncComponent, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import EasyDataTable from 'vue3-easy-data-table'
import { PhQrCode } from '@phosphor-icons/vue'

const QRCodeScanner = defineAsyncComponent(
  () => import('@/components/shared/QRCodeScanner/QRCodeScanner.vue')
)

const cl = inject('cl')
const { t } = useI18n()
const router = useRouter()
const customerStore = useCustomerStore()
const configuratorStore = useConfiguratorStore()
const uiStore = useUiStore()
const deviceStore = useDeviceStore()
const cartStore = useCartStore()
const notification = useNotification()
const { loadCustomerById } = useCustomer()
const { stockTransferSetDetails } = useStockTransfer()
const { getCustomerLastOrder } = usePackageDeal()
const searchInputRef = ref()
const searchDropdownRef = ref()
const query = ref('')
const customers = ref([])
const isSearching = ref(false)
const searchNoResults = ref(false)
const isChooseCartVisible = ref(false)
const hideChooseCartDialog = ref(false)
const isLoadingCustomer = ref(false)
const lastClients = ref([])
const redirectAfterLoad = ref(false)
const showMore = ref(false)
const isRefocusing = ref(false)
const showQrScanner = ref(false)

const carts = ref([])

const typesense = useTypesense()

defineEmits(['openAddNewCustomer'])

watchDebounced(
  query,
  () => {
    searchCustomer()
  },
  { debounce: 700, maxWait: 4000 }
)

async function searchCustomer() {
  if (query.value.length < 1) {
    return
  }

  if (isOrderNumber(query.value)) {
    const opened = await openOrderByNumber(query.value)
    if (opened) return
  } else if (isStockTransferNumber(query.value)) {
    const stockTransfer = await cl.stock_transfers.list({
      filters: { number_eq: query.value },
      include: [
        'sku',
        'origin_stock_location.address',
        'destination_stock_location',
        'shipment.order.customer',
        'shipment.order.billing_address',
        'shipment.attachments',
        'line_item.line_item_options'
      ]
    })

    if (stockTransfer[0]?.id) {
      handleClearInput()
      await stockTransferSetDetails(stockTransfer[0], false)
      uiStore.$patch({
        showStockTransferDetails: true
      })
      return
    }
  }

  try {
    isSearching.value = true
    customers.value = []

    const searchResults = await typesense
      .client()
      .collections(import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION)
      .documents()
      .search({
        q: query.value,
        query_by:
          'first_name,last_name,email,phone,address_line_1,address_line_2,address_zip_code,address_city',
        sort_by: `_text_match:desc`,
        limit: 50,
        include_fields: `id,first_name,last_name,email,phone,gender,language,birth_date,status,tags,restrictions,address_line_1,address_line_2,address_zip_code,address_city,country,reference`
      })

    searchNoResults.value = searchResults.hits.length === 0
    // if searchResults.hits.length > 0 then set customers.value = searchResults.hits. but only push the property document to the customers array.
    let results = searchResults.hits.length > 0 ? searchResults.hits.map((hit) => hit.document) : []

    // Filter out guest users for non-admin users
    const currentEmployee = deviceStore.settings.currentEmployee
    const isAdmin = ['admin'].includes(currentEmployee?.role)
    if (!isAdmin) {
      results = results.filter((customer) => !GUEST_USER_EMAILS.includes(customer.email))
    }

    customers.value = results

    // Keep the suggestions visible even if the dropdown was closed while typing
    // (e.g. a stray refocus auto-hide during the debounce window).
    uiStore.showCustomerSearchDialog = true
    if (searchDropdownRef.value) searchDropdownRef.value.open = true

    isSearching.value = false
  } catch (error) {
    isSearching.value = false
  }
}

function isOrderNumber(input) {
  // Check if input is a string and has more or equal to 8 characters
  if (typeof input !== 'string' || input.length < 8) {
    return false
  }

  // Check if every character in the input string is a digit (0-9)
  for (let i = 0; i < input.length; i++) {
    if (input[i] < '0' || input[i] > '9') {
      return false // Non-digit character found
    }
  }

  // If all characters are digits and length is exactly 8, return true
  return true
}

function isStockTransferNumber(input) {
  // check for  6 to 7 digits
  return /^[0-9]{6,7}$/.test(input)
}

async function openOrderByNumber(number) {
  const order = await cl.orders.list({
    include: ['customer'],
    filters: { number_eq: number },
    fields: { orders: ['number', 'customer_email', 'customer'], customers: ['id'] }
  })

  if (!order[0]?.id) return false

  handleClearInput()
  if (order[0].customer_email !== customerStore.customer?.email) {
    hideChooseCartDialog.value = true
    await loadCustomer(order[0].customer.id)
  }

  customerStore.customerOrderId = order[0].id
  uiStore.$patch({ currentCustomerDrawer: 'orders' })
  uiStore.$patch({ showCustomerOrderDetails: true })
  return true
}

function parseOrderNumberFromScan(value) {
  if (!value) return null
  const trimmed = String(value).trim()

  // Order numbers may carry suffixes like "12345678/S/001"; keep only the part before the first slash.
  const beforeSlash = trimmed.split('/')[0]
  if (isOrderNumber(beforeSlash)) return beforeSlash

  // Deep link URL: https://wings.shopviu.com/order-detail/<number>[/...]
  try {
    const url = new URL(trimmed)
    const match = url.pathname.match(/\/order-detail\/(\d+)/)
    if (match && isOrderNumber(match[1])) return match[1]
  } catch {
    // not a URL, fall through
  }

  return null
}

async function handleQrScanned(decodedText) {
  const orderNumber = parseOrderNumberFromScan(decodedText)
  if (!orderNumber) {
    notification.showNotification({
      message: t('qrScanner.invalidCode'),
      variant: 'warning',
      icon: 'exclamation-triangle'
    })
    return
  }

  const opened = await openOrderByNumber(orderNumber)
  if (!opened) {
    notification.showNotification({
      message: t('qrScanner.orderNotFound', { number: orderNumber }),
      variant: 'warning',
      icon: 'exclamation-triangle'
    })
  }
}

function closeDialog() {
  isChooseCartVisible.value = false
  customers.value = []

  if (uiStore.showCart) {
    uiStore.showCart = false
    router.push({ name: 'home' })
  }

  if (redirectAfterLoad.value) {
    router.push({ name: 'home' })
  }
}

async function loadCustomer(id) {
  let backUpCart = null
  redirectAfterLoad.value = customerStore.customer?.id ? true : false
  // anonymous cart present
  if (customerStore.getCurrentCart && !customerStore.customer?.id && !hideChooseCartDialog.value) {
    backUpCart = JSON.parse(JSON.stringify(customerStore.getCurrentCart))
    carts.value.push(backUpCart)
  }

  customerStore.reset()

  if (configuratorStore.config.prescription) {
    configuratorStore.$patch({
      config: {
        prescription: {
          id: null,
          prescription: {},
          prisma: ''
        }
      }
    })
  }

  if (configuratorStore.config.centering) {
    configuratorStore.$patch({
      config: {
        centering: {
          hsaGlass: null,
          hsaPrescription: null,
          fsw: null,
          inc: null,
          channel: null,
          viewHeightLeft: null,
          viewHeightRight: null
        }
      }
    })
  }

  isLoadingCustomer.value = true
  const customer = await loadCustomerById(id)
  if (!customer) {
    isLoadingCustomer.value = false
    notification.showNotification({
      message: 'Customer could not be loaded.',
      variant: 'danger',
      duration: 6000,
      icon: 'person-x'
    })
    return
  }

  const billingAddress = customer?.customer_addresses?.filter(
    (item) => item.address.metadata?.is_default_billing
  )
  const shippingAddress = customer?.customer_addresses?.filter(
    (item) => item.address.metadata?.is_default_shipping
  )

  if (billingAddress?.length) {
    cartStore.setBillingAddress(billingAddress[0].address)
  } else {
    if (customer?.customer_addresses.length > 0) {
      cartStore.setBillingAddress(customer.customer_addresses[0].address)
    } else {
      cartStore.setBillingAddress(null)
    }
  }

  if (shippingAddress?.length) {
    cartStore.setShippingAddress(shippingAddress[0].address)
  } else {
    if (customer?.customer_addresses.length > 0) {
      cartStore.setShippingAddress(customer.customer_addresses[0].address)
    } else {
      cartStore.setShippingAddress(null)
    }
  }

  if (customer?.metadata?.restrictions === CUSTOMER_RESTRICTION.BANNED) {
    isLoadingCustomer.value = false
    setTimeout(() => {
      uiStore.showCustomerBannedDialog = true
    }, 300)
  }
  if (customer?.metadata?.invalid_email) {
    isLoadingCustomer.value = false
    setTimeout(() => {
      uiStore.showEmailBannedOrInvalid = true
    }, 300)
  }

  if (!backUpCart) {
    uiStore.showCart = false
    carts.value = await getCarts(id)
  }

  if (customer) {
    setCustomerInTheLocalStorage({
      id: customer.id,
      firstName: customer.metadata?.first_name,
      lastName: customer.metadata?.last_name,
      email: customer.email,
      restrictions: customer.metadata?.restrictions
    })
  }

  isLoadingCustomer.value = false

  if (customer) {
    const orderForPackageDeal = await getCustomerLastOrder(
      id,
      deviceStore.settings?.country?.toUpperCase()
    )
    // count all customer orders
    try {
      customer.total_orders_count = await cl.orders.count({
        filters: {
          customer_id_eq: customer.id,
          order_copies_status_not_eq_or_null: 'completed',
          status_matches_any: 'placed,approved,cancelled',
          approved_at_or_placed_at_not_null: true
        }
      })
    } catch (error) {
      console.error('Error fetching customer orders count:', error)
    }
    // resets guest account for the cart
    customerStore.setGuestAccount({})
    customerStore.setCustomer(customer)
    customerStore.setCarts(carts.value)
    customerStore.setCustomerAddress(null)
    customerStore.setCustomerLastOrder(orderForPackageDeal)

    if (backUpCart) {
      // update order and set customer
      const cart = await cl.orders.update(
        {
          id: backUpCart.id,
          market: cl.markets.relationship(deviceStore.settings.marketId),
          customer: cl.customers.relationship(customerStore.customer.id),
          language_code:
            customerStore.customer?.metadata?.language?.toLowerCase() ||
            deviceStore.settings.language.toLowerCase()
        },
        {
          include: ['market']
        }
      )

      if (!cart) {
        notification.showNotification({
          message: 'Cart could not be assigned to this customer.',
          icon: 'x-circle'
        })
      } else {
        carts.value = await getCarts(id)
      }
    }

    // if customer is banned prevent displaying cart dialog
    if (uiStore.showCustomerBannedDialog) {
      isChooseCartVisible.value = false
      customerStore.isCustomerSelected = true
      return
    }

    if (carts.value.length > 0) {
      if (backUpCart) {
        customerStore.setCurrentCart(carts.value[0])
      } else {
        isChooseCartVisible.value = true
      }
    } else if (carts.value.length === 1) {
      customerStore.setCurrentCart(carts.value[0])
      isChooseCartVisible.value = false
      notification.showNotification({
        message: 'Customer has been loaded.',
        icon: 'person-down'
      })
    }
    cartStore.shippingAddressSetOnCart = false
    customerStore.isCustomerSelected = true
  } else {
    notification.showNotification({
      message: 'Customer could not be loaded.',
      variant: 'danger',
      duration: 6000,
      icon: 'person-x'
    })
  }

  if (redirectAfterLoad.value & !isChooseCartVisible.value) {
    router.push({ name: 'home' })
    uiStore.showCart = false
  }

  // Clear the search query after successfully loading a customer
  query.value = ''
  customers.value = []
}

async function getCarts(id) {
  return await cl.orders.list({
    include: [
      'attachments',
      'line_items.item',
      'line_items.line_item_options.sku_option',
      'market',
      'shipping_address',
      'billing_address',
      'shipments.available_shipping_methods',
      'tags'
    ],
    fields: {
      markets: ['code', 'metadata'],
      orders: [
        'attachments',
        'subtotal_amount_float',
        'total_amount_float',
        'currency_code',
        'fulfillment_status',
        'payment_status',
        'coupon_code',
        'status',
        'number',
        'created_at',
        'shipping_address',
        'billing_address',
        'metadata',
        'line_items',
        'market',
        'tags',
        'skus_count',
        'shipments',
        'gift_card_code',
        'total_amount_with_taxes_float',
        'shipping_amount_float'
      ]
    },
    filters: {
      customer_id_eq: `${id}`,
      status_matches_any: 'draft,pending',
      archived_at_null: true,
      market_merchant_address_country_code_eq: deviceStore.settings.country.toUpperCase(),
      line_items_item_type_eq: 'skus'
    },
    sort: { updated_at: 'desc' },
    pageSize: 3
  })
}

function handleCustomersFromLocalStorage() {
  let clients = getCustomersFromLocalStorage()

  // Filter out guest users for non-admin users
  const currentEmployee = deviceStore.settings.currentEmployee
  const isAdmin = ['admin'].includes(currentEmployee?.role)
  if (!isAdmin) {
    clients = clients.filter((customer) => !GUEST_USER_EMAILS.includes(customer.email))
  }

  lastClients.value = clients
}

function handleClearInput(event = null) {
  if (event) {
    event.stopPropagation()
  }

  // This is an explicit clear action, so always clear everything
  query.value = ''
  customers.value = []
  isSearching.value = false
  searchNoResults.value = false
  uiStore.showCustomerSearchDialog = false
}

// While the dropdown is open, Web Awesome listens for keydown on `document` and uses printable
// keys for type-ahead (focusing the matching item), which would swallow the characters typed
// into the search input. Let only the keys the dropdown needs for closing/navigation through.
const DROPDOWN_NAVIGATION_KEYS = ['Escape', 'ArrowDown', 'ArrowUp']

// Web Awesome moves focus to the first dropdown item once the menu has opened; the user is
// typing into the search input, so hand focus straight back.
function refocusSearchInput() {
  searchInputRef.value?.focus()
}

function handleSearchKeydown(event) {
  if (!DROPDOWN_NAVIGATION_KEYS.includes(event.key)) {
    event.stopPropagation()
  }
}

function handleCloseDropdown(event) {
  // If user is just refocusing the input, don't close the dropdown
  if (isRefocusing.value) {
    event.preventDefault()
    return
  }

  event.stopPropagation()

  // Only clear query if search suggestions are not open (no results shown)
  // If there are search results open, preserve the query when closing
  if (customers.value.length === 0 && !isSearching.value) {
    query.value = ''
  }

  uiStore.showCustomerSearchDialog = false
  searchNoResults.value = false
  handleCustomersFromLocalStorage()
}

function handleClick(event) {
  // Set flag to indicate we're refocusing the input
  isRefocusing.value = true

  handleCustomersFromLocalStorage()
  uiStore.showCustomerSearchDialog = true
  searchNoResults.value = false

  // Focus immediately in the same event loop to ensure keyboard shows on iOS
  requestAnimationFrame(() => {
    searchInputRef.value?.focus()
    // The clear-button click also bubbles into this handler, which can leave
    // showCustomerSearchDialog stranded `true` while Shoelace has already closed
    // the dropdown internally. Because the bound value never changes, the one-way
    // :open binding can't reopen it — so re-open imperatively to stay in sync.
    if (searchDropdownRef.value) searchDropdownRef.value.open = true
  })

  // Reset the flag after events settle
  setTimeout(() => {
    isRefocusing.value = false
  }, 100)
}

watch(
  () => uiStore.showCustomerSearchDialog,
  (value) => {
    if (value) {
      requestAnimationFrame(() => {
        searchInputRef.value?.focus()
      })
    }
  }
)
</script>

<style src="./CustomerSearch.css" scoped />
<style scoped lang="postcss">
wa-dropdown-item {
  font-size: var(--wa-font-size-s);
}
</style>

<style lang="postcss">
.customer-table {
  font-size: var(--wa-font-size-s);
  line-height: var(--wa-line-height-condensed);

  th {
    font-weight: 400;
  }

  .vue3-easy-data-table__header th.sortable.none .sortType-icon {
    opacity: 1 !important;
  }
}
</style>
